// @ts-nocheck
import { SettingsCard } from './SettingsPrimitives';
import { IntegrationRow } from './IntegrationRow';
import { useState, useEffect } from 'react';
import { Loader2, Check, AlertCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { SwoftLogo } from '@/components/common/SwoftLogo';
import { GmailMark, OutlookMark } from '@/components/common/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { updateCustomer } from '@/api/endpoints/customers';
import {
  chooseSwoftOrganisation,
  disconnectSwoftConnect,
  getSwoftConnectHandover,
  getSwoftConnectStatus,
  setSwoftMeetingOwner,
  swoftConnectAuthorizeUrl,
} from '@/api/endpoints/swoftConnect';
import { useQueryClient } from '@tanstack/react-query';
import { oauthReasonMessage } from '@/lib/oauthReason';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';
import { usePermissions } from '@/hooks/usePermissions';

// ─── Constants ────────────────────────────────────────────────────────────────

// ─── SWOFT CRM: connect in one click ─────────────────────────────────────────
//
// Kevin, 2026-09-23: connecting a dialer to the Swoft CRM must be one button
// and one choice, not five fields to copy by hand. The field that made the
// hand-copying dangerous is the organisation identifier: get it wrong and a
// client's calls are written into another client's CRM, with nothing
// downstream noticing.
//
// So the organisation is never typed. The person consents on Swoft, Swoft
// says which organisations they belong to, they pick one, and SWOFT decides
// whether that pick holds (403 when it does not). The list shown below has no
// authority at all.
//
// The hand-typed form is kept, folded, so a client is never blocked when the
// one-click path is unavailable or fails.

// ─── SWOFT CRM: who receives this client's meetings ──────────────────────────
//
// Kevin, 2026-09-24: the caller books the meeting, but a sales rep of the
// client holds it, and nothing named which one. The picker offers the
// organisation's roster snapshotted when the connection was made (never a
// typed identifier: a wrong one puts a client's meetings on a stranger's
// calendar). It shows up right after the organisation is chosen, and stays
// changeable on the card at any time, without a second trip through Swoft.

export function MeetingOwnerPicker({ members, ownerPersonId, ownerName, busy, onChoose }) {
  const { t } = useTranslation(['settings', 'common']);
  const [open, setOpen] = useState(false);
  const roster = members || [];
  const hasRoster = roster.length > 0;

  return (
    <div className="space-y-2 pt-3 border-t border-border/70" data-testid="swoft-meeting-owner">
      <div>
        <p className="text-sm font-semibold text-foreground">{t('settings:swoftConnect.meetingOwnerTitle')}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{t('settings:swoftConnect.meetingOwnerHint')}</p>
      </div>

      {ownerPersonId ? (
        <p className="text-sm text-foreground" data-testid="swoft-meeting-owner-current">
          {t('settings:swoftConnect.meetingOwnerCurrent', { name: ownerName || ownerPersonId })}
        </p>
      ) : (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5" data-testid="swoft-meeting-owner-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">{t('settings:swoftConnect.meetingOwnerNoneWarning')}</p>
        </div>
      )}

      {hasRoster ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" disabled={busy} data-testid="swoft-meeting-owner-trigger">
              {busy
                ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:status.checking')}</>
                : (ownerPersonId ? t('settings:swoftConnect.meetingOwnerChangeButton') : t('settings:swoftConnect.meetingOwnerPickPlaceholder'))}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-64" align="start">
            <Command>
              <CommandInput placeholder={t('settings:swoftConnect.meetingOwnerSearchPlaceholder')} />
              <CommandList>
                <CommandEmpty>{t('settings:swoftConnect.meetingOwnerNoResults')}</CommandEmpty>
                <CommandGroup>
                  {ownerPersonId && (
                    <CommandItem
                      value="__none__"
                      data-testid="swoft-meeting-owner-option-none"
                      onSelect={() => { setOpen(false); onChoose(''); }}
                    >
                      {t('settings:swoftConnect.meetingOwnerClearOption')}
                    </CommandItem>
                  )}
                  {roster.map((member) => (
                    <CommandItem
                      key={member.personId}
                      value={`${member.displayName} ${member.emailAddress || ''}`}
                      data-testid={`swoft-meeting-owner-option-${member.personId}`}
                      onSelect={() => { setOpen(false); onChoose(member.personId); }}
                    >
                      {member.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="swoft-meeting-owner-empty-roster">
          {t('settings:swoftConnect.meetingOwnerEmptyRoster')}
        </p>
      )}
    </div>
  );
}

export function SwoftCRMConfig({ clientId, clientEmail, currentClient, swoftCrmStatus, swoftCrmLoading, swoftCrmConnected }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  // What the callback route put in the URL when the browser came back from
  // Swoft. Read once, then wiped so a reload does not replay it.
  const [handoverId, setHandoverId] = useState('');
  const [returnError, setReturnError] = useState('');
  const [chosenOrg, setChosenOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [meetingOwnerBusy, setMeetingOwnerBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('swoft');
    if (!outcome) return;
    if (outcome === 'choose' && params.get('handover')) {
      setHandoverId(params.get('handover'));
      setReturnError('');
    } else if (outcome === 'error') {
      // A fixed code since AUDIT-INT-15: never display the raw value.
      setReturnError(params.get('reason') ? oauthReasonMessage(t, params.get('reason')) : t('settings:swoftConnect.genericError'));
      setManualOpen(true);
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [t]);

  const { data: connectStatus, isLoading: connectLoading } = useQuery({
    queryKey: ['swoft-connect-status', clientId || '_none'],
    queryFn: () => getSwoftConnectStatus(clientId),
    enabled: !!clientId,
    staleTime: 15_000,
  });

  const { data: handover } = useQuery({
    queryKey: ['swoft-connect-handover', handoverId],
    queryFn: () => getSwoftConnectHandover(handoverId),
    enabled: !!handoverId,
    retry: false,
  });

  useEffect(() => {
    if (handover) setChosenOrg(handover.preselectedOrganisationId || '');
  }, [handover]);

  const connected = connectStatus?.connected ?? swoftCrmConnected;
  const oneClickAvailable = connectStatus?.oneClickAvailable ?? false;

  const handleConnect = () => {
    if (!clientId) {
      toast.error(t('settings:swoftCrm.clientNotFound'));
      return;
    }
    // Full page navigation: the route answers a 302 towards the Swoft consent
    // screen, which fetch cannot follow on the person's behalf.
    window.location.href = swoftConnectAuthorizeUrl(clientId);
  };

  const handleConfirm = async () => {
    if (!handoverId || !chosenOrg) return;
    setBusy(true);
    setReturnError('');
    try {
      const status = await chooseSwoftOrganisation(handoverId, chosenOrg);
      setHandoverId('');
      // Written synchronously so the roster (and the "who receives the
      // meetings" question right below) show without waiting on a refetch:
      // it is the continuation of the same connecting gesture, not a second
      // screen.
      queryClient.setQueryData(['swoft-connect-status', clientId || '_none'], status);
      queryClient.invalidateQueries({ queryKey: ['swoft-connect-status'] });
      queryClient.invalidateQueries({ queryKey: ['swoft-crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('settings:swoftConnect.connectedToast', { name: status.organisationName || status.organisationId }));
    } catch (e) {
      // A refusal from Swoft carries its own sentence, naming the
      // organisation. It is shown as is rather than turned into a code.
      setReturnError(e?.message || t('settings:swoftConnect.genericError'));
    } finally {
      setBusy(false);
    }
  };

  const handleSetMeetingOwner = async (personId) => {
    if (!clientId) return;
    setMeetingOwnerBusy(true);
    try {
      const status = await setSwoftMeetingOwner(clientId, personId);
      queryClient.setQueryData(['swoft-connect-status', clientId || '_none'], status);
      queryClient.invalidateQueries({ queryKey: ['swoft-connect-status'] });
      if (personId) {
        toast.success(t('settings:swoftConnect.meetingOwnerSavedToast', { name: status.meetingOwnerName || status.meetingOwnerPersonId }));
      } else {
        toast.success(t('settings:swoftConnect.meetingOwnerClearedToast'));
      }
    } catch (e) {
      toast.error(e?.message || t('settings:swoftConnect.meetingOwnerErrorToast'));
    } finally {
      setMeetingOwnerBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      await disconnectSwoftConnect(clientId);
      queryClient.invalidateQueries({ queryKey: ['swoft-connect-status'] });
      queryClient.invalidateQueries({ queryKey: ['swoft-crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('settings:swoftConnect.disconnectedToast'));
    } catch (e) {
      toast.error(t('settings:toasts.error', { message: e.message || e }));
    } finally {
      setBusy(false);
    }
  };

  const organisationLiee = connectStatus?.organisationName
    || connectStatus?.organisationId
    || currentClient?.swoft_crm_tenant_id
    || '';
  const verification = connectLoading || swoftCrmLoading;
  // Le detail ne s'affiche que s'il a quelque chose a dire : une erreur de
  // retour, une organisation a choisir, ou les reglages d'une connexion
  // etablie. Sinon la ligne se suffit.
  const detailAMontrer = Boolean(returnError) || Boolean(handoverId) || connected;

  return (
    <div data-testid="swoft-connect">
      <SettingsCard>
        {/* Le CRM se connecte comme une boite mail : une ligne, un bouton. */}
        <IntegrationRow
          logo={<SwoftLogo size={20} />}
          name="Swoft CRM"
          description={connected && organisationLiee
            ? t('settings:swoftConnect.connectedTo', { name: organisationLiee })
            : t('settings:swoftCrm.subtitle')}
          state={verification ? 'checking' : connected ? 'connected' : oneClickAvailable ? 'disconnected' : 'unavailable'}
          stateLabel={t(
            verification ? 'settings:status.checking'
              : connected ? 'common:states.connected'
                : oneClickAvailable ? 'settings:status.notConnected'
                  : 'settings:email.notConfiguredShort',
          )}
          action={connected ? (
            <Button size="sm" variant="outline" className="h-8" onClick={handleDisconnect} disabled={busy}>
              {busy
                // i18n-exempt: CSS classes, not text
                ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:status.disconnecting')}</>
                : t('settings:status.disconnect')}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={handleConnect}
              disabled={!clientId || !oneClickAvailable || connectLoading}
              title={oneClickAvailable ? '' : t('settings:swoftConnect.unavailableHint')}
            >
              {t('settings:status.connect')}
            </Button>
          )}
          last={!detailAMontrer}
        />

        {detailAMontrer && (
        <div className="px-5 py-4 space-y-4">
          {returnError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/70 px-3 py-2.5">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-xs text-red-800" data-testid="swoft-connect-error">{returnError}</p>
            </div>
          )}

          {/* The organisation picker, after the person consented on Swoft. The
              handover names its own Customer, so switching client in the
              sidebar does not show someone else's pending choice. */}
          {handoverId && handover && handover.customerId === clientId && !connected && (
            <div className="space-y-3" data-testid="swoft-connect-choose">
              <div>
                <p className="text-sm font-semibold text-foreground">{t('settings:swoftConnect.chooseTitle')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {handover.organisations.length === 1
                    ? t('settings:swoftConnect.chooseSingleHint')
                    : t('settings:swoftConnect.chooseHint')}
                </p>
              </div>
              <RadioGroup value={chosenOrg} onValueChange={setChosenOrg} className="space-y-1.5">
                {handover.organisations.map((org) => (
                  <label
                    key={org.id}
                    className="flex items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5 cursor-pointer hover:bg-muted/40"
                  >
                    <RadioGroupItem value={org.id} id={`swoft-org-${org.id}`} />
                    <span className="text-sm font-medium text-foreground">{org.name}</span>
                  </label>
                ))}
              </RadioGroup>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleConfirm} disabled={busy || !chosenOrg}>
                  {busy
                    ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:swoftConnect.confirming')}</>
                    : t('settings:swoftConnect.confirm')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setHandoverId('')} disabled={busy}>
                  {t('common:actions.cancel')}
                </Button>
              </div>
            </div>
          )}

          {/* Connected: which organisation, and how to cut it. */}
          {connected && !handoverId && (
            <div className="space-y-1">
              {/* L'organisation connectee et le bouton de deconnexion sont
                  sur la ligne ci-dessus ; il ne reste ici que le reglage. */}
              <MeetingOwnerPicker
                members={connectStatus?.members || []}
                ownerPersonId={connectStatus?.meetingOwnerPersonId || ''}
                ownerName={connectStatus?.meetingOwnerName || ''}
                busy={meetingOwnerBusy}
                onChoose={handleSetMeetingOwner}
              />
            </div>
          )}

        </div>
        )}
      </SettingsCard>

      {/* The hand-typed fallback, folded. It is never removed: a client must
          stay connectable when the one-click path is unavailable. */}
      <div className="mt-3">
        <button
          type="button"
          className="text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
          onClick={() => setManualOpen((open) => !open)}
          data-testid="swoft-manual-toggle"
        >
          {manualOpen ? t('settings:swoftConnect.manualHide') : t('settings:swoftConnect.manualShow')}
        </button>
        {manualOpen && (
          <div className="mt-3">
            <SwoftManualConfig
              clientId={clientId}
              clientEmail={clientEmail}
              currentClient={currentClient}
              swoftCrmStatus={swoftCrmStatus}
              swoftCrmLoading={swoftCrmLoading}
              swoftCrmConnected={swoftCrmConnected}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SWOFT CRM: the hand-typed fallback (inline component, local state) ──────

const DEFAULT_SWOFT_CRM_URL = import.meta.env.VITE_SWOFT_CRM_DEFAULT_URL || '';
const DEFAULT_SWOFT_CRM_TENANT = import.meta.env.VITE_SWOFT_CRM_DEFAULT_TENANT || '';

function SwoftManualConfig({ clientId, clientEmail, currentClient, swoftCrmStatus, swoftCrmLoading, swoftCrmConnected }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(currentClient?.swoft_crm_url || DEFAULT_SWOFT_CRM_URL);
  const [apiKey, setApiKey] = useState('');
  const [tenantId, setTenantId] = useState(currentClient?.swoft_crm_tenant_id || DEFAULT_SWOFT_CRM_TENANT);
  // ADR-036: OAuth client id (client_credentials) and the secret Swoft signs
  // its inbound events with. Both are write-only: the server never returns them.
  const [swoftClientId, setSwoftClientId] = useState('');
  const [eventsSecret, setEventsSecret] = useState('');
  // Opt-in (2026-09-22): by default the caller sends each qualified call with
  // the "Envoyer au CRM" button; this switch makes the server push every one.
  const [autoSend, setAutoSend] = useState(!!currentClient?.auto_send_call_activity);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (currentClient) {
      setUrl(currentClient.swoft_crm_url || DEFAULT_SWOFT_CRM_URL);
      setTenantId(currentClient.swoft_crm_tenant_id || DEFAULT_SWOFT_CRM_TENANT);
      setAutoSend(!!currentClient.auto_send_call_activity);
    }
  }, [currentClient]);

  const handleAutoSendChange = async (checked) => {
    if (!clientId) {
      toast.error(t('settings:swoftCrm.clientNotFound'));
      return;
    }
    setAutoSend(checked);
    try {
      await updateCustomer(clientId, { auto_send_call_activity: checked });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(checked ? t('settings:swoftCrm.autoSendOn') : t('settings:swoftCrm.autoSendOff'));
    } catch (e) {
      setAutoSend(!checked);
      toast.error(t('settings:toasts.error', { message: e.message || e }));
    }
  };

  const handleSave = async () => {
    if (!clientId) {
      toast.error(t('settings:swoftCrm.cannotSave'));
      return;
    }
    setSaving(true);
    try {
      const update = {
        swoft_crm_enabled: true,
        swoft_crm_url: url.trim(),
        swoft_crm_tenant_id: tenantId.trim(),
      };
      if (apiKey.trim()) update.swoft_crm_api_key = apiKey.trim();
      if (swoftClientId.trim()) update.swoft_client_id = swoftClientId.trim();
      if (eventsSecret.trim()) update.swoft_events_secret = eventsSecret.trim();
      await updateCustomer(clientId, update);
      queryClient.invalidateQueries({ queryKey: ['swoft-crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('settings:swoftCrm.saved'));
    } catch (e) {
      toast.error(t('settings:toasts.error', { message: e.message || e }));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!clientId) {
      toast.error(t('settings:swoftCrm.cannotTest'));
      return;
    }
    setTesting(true);
    try {
      // Save first, then test
      const update = { swoft_crm_enabled: true, swoft_crm_url: url.trim(), swoft_crm_tenant_id: tenantId.trim() };
      if (apiKey.trim()) update.swoft_crm_api_key = apiKey.trim();
      if (swoftClientId.trim()) update.swoft_client_id = swoftClientId.trim();
      if (eventsSecret.trim()) update.swoft_events_secret = eventsSecret.trim();
      await updateCustomer(clientId, update);
      const result = await api.functions.invoke('getSwoftCRMStatus', { clientEmail });
      const status = result?.data || result;
      queryClient.invalidateQueries({ queryKey: ['swoft-crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (status?.connected) {
        toast.success(t('settings:swoftCrm.testOk'));
      } else {
        toast.error(t('settings:swoftCrm.testFailed'));
      }
    } catch (e) {
      toast.error(t('settings:toasts.error', { message: e.message || e }));
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      await updateCustomer(clientId, {
        swoft_crm_enabled: false,
        swoft_crm_url: '',
        swoft_crm_api_key: '',
        swoft_crm_tenant_id: '',
        swoft_client_id: '',
        swoft_events_secret: '',
      });
      setUrl('');
      setApiKey('');
      setTenantId('');
      setSwoftClientId('');
      setEventsSecret('');
      queryClient.invalidateQueries({ queryKey: ['swoft-crm-status'] });
      toast.success(t('settings:swoftCrm.disconnected'));
    } catch (e) {
      toast.error(t('settings:toasts.error', { message: e.message || e }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('settings:swoftConnect.manualHeading')}</p>
      <SettingsCard>
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <SwoftLogo size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">SWOFT CRM</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('settings:swoftCrm.subtitle')}</p>
          </div>
          {swoftCrmLoading ? (
            <Badge className="text-[10px] bg-muted text-muted-foreground">{t('settings:status.checking')}</Badge>
          ) : (
            <Badge className={`text-[10px] ${swoftCrmConnected ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
              {swoftCrmConnected ? t('common:states.connected') : t('settings:status.notConnected')}
            </Badge>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* URL */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">{t('settings:swoftCrm.apiUrl')}</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={DEFAULT_SWOFT_CRM_URL || 'https://...'}
              className="h-9"
            />
          </div>

          {/* OAuth client id (ADR-036) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">{t('settings:swoftCrm.oauthClientId')}</label>
            <Input
              value={swoftClientId}
              onChange={(e) => setSwoftClientId(e.target.value)}
              placeholder={swoftCrmConnected ? t('settings:swoftCrm.unchangedPlaceholder') : 'client-...'}
              className="h-9"
            />
          </div>

          {/* API Key */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">{t('settings:swoftCrm.oauthClientSecret')}</label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={swoftCrmConnected ? '••••••••••••' : 'sk_admin_...'}
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">{t('settings:swoftCrm.secretNote')}</p>
          </div>

          {/* Inbound events secret (ADR-036) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">{t('settings:swoftCrm.eventsSecret')}</label>
            <Input
              type="password"
              value={eventsSecret}
              onChange={(e) => setEventsSecret(e.target.value)}
              placeholder={swoftCrmConnected ? '••••••••••••' : t('settings:swoftCrm.eventsSecretPlaceholder')}
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">{t('settings:swoftCrm.eventsSecretNote')}</p>
          </div>

          {/* Tenant ID */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">{t('settings:swoftCrm.tenantId')}</label>
            <Input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              // i18n-exempt: an identifier prefix, not text
              placeholder="biz-…"
              className="h-9"
            />
          </div>

          {/* Automatic send opt-in */}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">{t('settings:swoftCrm.autoSendLabel')}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{t('settings:swoftCrm.autoSendHint')}</p>
            </div>
            <Switch checked={autoSend} onCheckedChange={handleAutoSendChange} disabled={!clientId} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={handleSave} disabled={saving || !url}>
              {saving ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('common:actions.saving')}</> : t('common:actions.save')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleTest} disabled={testing || !url}>
              {testing ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:swoftCrm.testing')}</> : t('settings:swoftCrm.testConnection')}
            </Button>
            {swoftCrmConnected && (
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" onClick={handleDisconnect} disabled={saving}>
                {t('settings:status.disconnect')}
              </Button>
            )}
          </div>

          {/* Status info */}
          {swoftCrmConnected && (
            <div className="flex items-center gap-2 pt-2 border-t border-border/50">
              <Check className="w-4 h-4 text-green-600" />
              <p className="text-sm text-foreground font-medium">{t('settings:swoftCrm.connectedTo', { url: swoftCrmStatus?.url || url })}</p>
            </div>
          )}
        </div>
      </SettingsCard>
    </div>
  );
}

// ─── The client's meeting link (DUP-1) ──────────────────────────────────────
//
// customer.calendly_url: opened from a prospect's record when no CRM books
// the meeting. One writer in the dialer, here; an administrator edits it
// (saved on blur or Enter, only when it changed, toast on the server's
// answer), everyone else reads it.
export function CalendarLinkRow({ customer, canEdit }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const currentUrl = customer?.calendly_url || '';
  const [value, setValue] = useState(currentUrl);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(currentUrl); }, [currentUrl]);

  const save = async () => {
    const next = value.trim();
    if (!customer || next === currentUrl) return;
    setSaving(true);
    try {
      await updateCustomer(customer.id, { calendly_url: next });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('settings:toasts.saved'), { duration: 1500 });
    } catch (e) {
      toast.error(errorMessage(e, t, 'settings:integrations.calendarSaveError'), errorToastOptions(e));
      setValue(currentUrl);
    } finally {
      setSaving(false);
    }
  };

  const label = t('settings:integrations.calendarLabel', { name: customer.company_name });
  return (
    <div className="px-5 py-4 flex items-center justify-between gap-4" data-testid="calendar-link-row">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{t('settings:integrations.calendarHint')}</p>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {canEdit ? (
          <Input
            className="w-72 h-9 text-xs"
            aria-label={label}
            placeholder="https://cal.com/..."
            value={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => void save()}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
        ) : (
          <span className="text-sm text-muted-foreground truncate max-w-72" data-testid="calendar-link-readonly">
            {currentUrl || t('settings:integrations.calendarNone')}
          </span>
        )}
        {currentUrl && (
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:text-foreground"
            aria-label={t('settings:integrations.calendarOpen')}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Section: Intégrations ────────────────────────────────────────────────────

// ─── Card: Email integration (per-caller OAuth Gmail / Outlook) ───────────
//
// Le caller connecte son compte Gmail OU Outlook. L'email part ensuite
// depuis sa propre boîte (délivrabilité native, identité respectée,
// reply threadé chez lui). Pattern industrie standard (Salesloft,
// Outreach, Apollo). Switch entre les 2 providers est mutex côté domain.
function EmailIntegrationCard() {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['email-integration-status'],
    queryFn: async () => {
      const r = await api.functions.invoke('emailIntegrationStatus');
      return r?.data ?? r;
    },
    staleTime: 30_000,
  });

  // Re-fetch when we come back from an OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('integration') === 'gmail' || params.get('integration') === 'outlook') {
      const result = params.get('status');
      if (result === 'connected') toast.success(t('settings:email.connectedToast', { provider: params.get('integration') }));
      if (result === 'denied') toast.error(t('settings:email.canceled'));
      // AUDIT-INT-10: a failed callback now comes back here with a reason
      // code instead of a JSON page on the API host.
      if (result === 'error') toast.error(oauthReasonMessage(t, params.get('reason')));
      queryClient.invalidateQueries({ queryKey: ['email-integration-status'] });
      queryClient.invalidateQueries({ queryKey: ['email-capabilities'] });
      // Clean the URL
      const url = new URL(window.location.href);
      url.searchParams.delete('integration');
      url.searchParams.delete('status');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, [queryClient, t]);

  const provider = status?.provider as 'gmail' | 'outlook' | null;
  const connectedEmail = status?.email as string | null;
  const gmailConfigured = !!status?.gmailConfigured;
  const outlookConfigured = !!status?.outlookConfigured;

  const disconnect = async () => {
    try {
      await api.functions.invoke('emailIntegrationDisconnect');
      queryClient.invalidateQueries({ queryKey: ['email-integration-status'] });
      queryClient.invalidateQueries({ queryKey: ['email-capabilities'] });
      toast.success(t('settings:email.disconnected'));
    } catch {
      toast.error(t('settings:email.disconnectError'));
    }
  };

  // Une ligne par boite mail, exactement comme le CRM : marque, ce qu'elle
  // fait, etat, un geste. Les deux gros boutons pleine largeur precedents
  // faisaient de l'e-mail un cas a part sur une page d'integrations.
  const ligne = (cle, Marque, nom, configure) => {
    const branchee = provider === cle;
    const etat = isLoading ? 'checking' : branchee ? 'connected' : configure ? 'disconnected' : 'unavailable';
    return (
      <IntegrationRow
        key={cle}
        logo={<Marque size={18} />}
        name={nom}
        // W4 (quick win 13): the reason an integration cannot be connected
        // is written under its name. It used to hide in the title of a
        // disabled button, which never shows its tooltip.
        description={branchee ? connectedEmail : etat === 'unavailable' ? t('settings:email.unavailableReason') : t(`settings:email.${cle}Description`)}
        state={etat}
        stateLabel={t(
          etat === 'checking' ? 'settings:status.checking'
            : etat === 'connected' ? 'common:states.connected'
              : etat === 'unavailable' ? 'settings:email.notConfiguredShort'
                : 'settings:status.notConnected',
        )}
        action={branchee ? (
          <Button size="sm" variant="outline" className="h-8" onClick={disconnect}>
            {t('settings:status.disconnect')}
          </Button>
        ) : etat === 'unavailable' ? undefined : (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={isLoading}
            // i18n-exempt: a URL, not text
            onClick={() => { window.location.href = `/api/integrations/${cle}/oauth/authorize`; }}
          >
            {t('settings:status.connect')}
          </Button>
        )}
        last={cle === 'outlook'}
      />
    );
  };

  return (
    <SettingsCard>
      {ligne('gmail', GmailMark, 'Gmail', gmailConfigured)}
      {ligne('outlook', OutlookMark, 'Outlook', outlookConfigured)}
    </SettingsCard>
  );
}

export function IntegrationsSection({ d, customers }) {
  const { t } = useTranslation(['settings', 'common']);
  // ADR-031 — CRM is per-Customer. Operate on the Customer currently selected
  // in the sidebar; the legacy tenant-wide Client doc no longer exists.
  const currentCustomer = customers?.find((c) => c.id === d.selectedCustomerId);
  const hsEmail = d.callerData?.client_email || d.callerData?.email;


  const { data: swoftCrmStatus, isLoading: swoftCrmLoading } = useQuery({
    queryKey: ['swoft-crm-status', hsEmail || '_self'],
    queryFn: () =>
      api.functions.invoke('getSwoftCRMStatus', { clientEmail: hsEmail }).then((r) => r.data),
    enabled: !!hsEmail,
  });

  // The Swoft connection is per Customer since the one-click flow: the
  // legacy status above answers for the tenant self-link, which says nothing
  // about the Customer selected in the sidebar.
  const { data: swoftConnectStatus } = useQuery({
    queryKey: ['swoft-connect-status', currentCustomer?.id || '_none'],
    queryFn: () => getSwoftConnectStatus(currentCustomer.id),
    enabled: !!currentCustomer?.id,
    staleTime: 15_000,
  });

  const swoftCrmConnected = swoftCrmStatus?.connected === true || swoftConnectStatus?.connected === true;
  const { isTenantAdmin } = usePermissions();


  return (
    <div className="space-y-8">
      {/* 🚨 Kevin, 2026-09-30 : deux categories, et rien d'autre. Ont disparu
          de cet ecran, parce qu'ils n'agissaient sur rien de lisible :
          - le selecteur « CRM actif », qui doublait le fait de connecter ou
            non un CRM : on est connecte a Swoft, ou on ne l'est pas ;
          - le bandeau « Integrations pour <societe> », qui redisait le client
            deja choisi dans la barre laterale, avec son initiale dans un
            carre. Il affichait en plus une raison sociale perimee.
          HubSpot reste hors de cette page tant qu'il n'est pas propose. */}

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('settings:integrations.categoryEmail')}
        </h3>
        <EmailIntegrationCard />
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('settings:integrations.categoryCrm')}
        </h3>
        {!currentCustomer && (
          <p className="text-sm text-amber-900 bg-amber-50/60 border border-amber-200/50 rounded-xl px-4 py-3">
            {t('settings:integrations.noClientSelected')}
          </p>
        )}
        {!isTenantAdmin && (
          <p className="text-sm text-muted-foreground" data-testid="integrations-admin-only">
            {t('settings:integrations.adminOnly')}
          </p>
        )}
        <SwoftCRMConfig
          clientId={currentCustomer?.id}
          clientEmail={hsEmail}
          currentClient={currentCustomer}
          swoftCrmStatus={swoftCrmStatus}
          swoftCrmLoading={swoftCrmLoading}
          swoftCrmConnected={swoftCrmConnected}
        />
      </section>

      {/* DUP-1: the client's meeting link has one writer in the dialer, here,
          for an administrator (PATCH /api/customers/:id is tenant_admin).
          Général no longer edits it. */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('settings:integrations.categoryBooking')}
        </h3>
        {currentCustomer ? (
          <SettingsCard>
            <CalendarLinkRow customer={currentCustomer} canEdit={isTenantAdmin} />
          </SettingsCard>
        ) : (
          <p className="text-sm text-muted-foreground">{t('settings:integrations.noClientSelected')}</p>
        )}
      </section>
    </div>
  );
}
